import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import breakageData from "@/data/breakageBoard.json";
import { BreakageClient } from "./BreakageClient";
import { Card } from "@/design/primitives";
import { AuthGate } from "@/screens/cc";


export default function BreakageClientPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("breakage");
  const [dismissed, setDismissed] = useState(false);

  const bakedItems = useMemo(() => {
    const rail = (breakageData.rail || []).map((r) => ({ ...r, payload: r.payload || {} }));
    const cards = (breakageData.cards || []).map((c) => ({ ...c, payload: c.payload || {} }));
    return [...rail, ...cards];
  }, []);

  const liveItems = useMemo(() => (board.items || []).map((i) => {
    // Live store rows carry the card inside payload (IngestItem shape); the client
    // reads card fields at top level (BeatRow/Reader). Lift once, at the page edge.
    const p = i.payload || {};
    if (i.kind !== "break-rail" && i.kind !== "break-brand" && p.id && p.name && !i.name) {
      return { ...p, ...i };
    }
    return i;
  }), [board.items]);
  const viewItems = board.live && liveItems.length ? liveItems : bakedItems;
  const viewMeta = board.live && board.meta ? board.meta : breakageData.stamp;

  if (board.status === "needs-signin") {
    return (
      <div data-testid="breakage-page" className="w-full font-inter text-os-ink">
        <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
          <AuthGate />
          <BreakageClient items={bakedItems} meta={breakageData.stamp} board={board} variant={desktop ? "desktop" : "mobile"} onNotice={() => {}} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="breakage-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {!board.live && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit's breakage feed is offline this visit — the page shows the last committed state.
            </p>
          </Card>
        )}
        {board.live && !dismissed && !board.meta?.ingested_at && (
          <button
            type="button"
            data-testid="breakage-sample-note"
            onClick={() => setDismissed(true)}
            className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
          >
            Today this board is a hand-built sample — Kit's live breakage sync arrives on the first fetch. Tap to dismiss.
          </button>
        )}
        <BreakageClient items={viewItems} meta={viewMeta} board={board} variant={desktop ? "desktop" : "mobile"} onNotice={board.setNotice} />
        {board.notice?.text && (
          <p className="px-1 text-[0.8125rem] text-os-amber" data-testid="breakage-notice">{board.notice.text}</p>
        )}
      </div>
    </div>
  );
}
